import React, { type ReactNode, Suspense } from 'react';
import { connect } from 'react-redux';
import { v7 } from 'uuid';

import { createLog } from '../apis/logger';
import { type RootState } from '../store/store';
import history from '../utils/history/customHistory';
import { selectLocaleToString } from '../store/selectors/i18n.selectors';
import { selectInterventionDocNumber } from '../store/selectors/intervention';
import { selectProductId } from '../store/selectors/catalog';

const ErrorPage = React.lazy(async () => ({
  default: (await import('./ErrorPage/ErrorPage')).ErrorPage,
}));

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly docNumber?: string;
  readonly language: string;
  readonly productCode?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  correlationId?: string;
}
const logger = createLog('AppError');

export class AppErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error) {
    const correlationId = v7();
    const { docNumber, language, productCode } = this.props;

    void logger.log(error, {
      correlation_id: correlationId,
      uri: history.location.pathname,
      invoice_number: docNumber,
      language,
      product_code: productCode,
    });
    this.setState(state => ({ ...state, correlationId }));
  }

  render() {
    const { hasError, correlationId } = this.state;
    const { children } = this.props;
    return hasError ? (
      <Suspense fallback="...">
        <ErrorPage correlationId={correlationId} />
      </Suspense>
    ) : (
      children
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  docNumber: selectInterventionDocNumber(state),
  language: selectLocaleToString(state),
  productCode: selectProductId(state),
});

export default connect(mapStateToProps)(AppErrorBoundary);
